import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import { MediaList } from '@euler/app/tv/components/MediaList';
import { TaskScreenContent } from '@euler/app/tv/components/TaskScreenContent';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Colors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { computeGridLayoutItemSize } from '@euler/functions/layoutMetrics';
import { MediaSourceTypeTextMap } from '@euler/functions/mediaSourceUtil';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  MediaObject,
  MediaWithSource,
  mediaWithSourceToMediaObject,
} from '@euler/model';
import { ResourceAccessScope } from '@euler/model/enum';
import { MediaSourceType } from '@euler/model/report';
import { useServiceFactory } from '@euler/services/factory';
import { comparerDesc } from '@euler/utils';
import { arr2groupmap } from '@euler/utils/array';
import { MotiView } from 'moti';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';

const kCellMargin = 32;
const kMaxCellWidth = 320;

type Section = {
  mileage: number;
  data: MediaWithSource[];
};

export const MediasScreen = wrapNavigatorScreen(() => {
  const appLoading = useAppLoading();
  const { detail } = useTaskContext();
  const { vehicleInfoService } = useServiceFactory();
  const { licensePlateNo, vin } = detail.basicInfo;
  const [medias, setMedias] = useState<MediaWithSource[]>();

  useEffect(() => {
    void appLoading.showWithAction(async () => {
      const res = await vehicleInfoService.searchMedias({
        scope: ResourceAccessScope.Org,
        licensePlateNo,
        vin,
      });
      setMedias(res);
    });
  }, [appLoading, licensePlateNo, vehicleInfoService, vin]);

  return (
    <TaskScreenContent>
      <Content medias={medias} />
    </TaskScreenContent>
  );
});

const Content = memo(
  ({ medias }: { medias: MediaWithSource[] | undefined }) => {
    const { rpx } = useResponsiveContext();
    const { width } = useWindowDimensions();
    const [paddingLeft, paddingRight] = useScreenContentPadding();
    const cellMargin = rpx(kCellMargin);

    const sections = useMemo(() => {
      const sorted = [...(medias ?? [])].sort((x, y) =>
        comparerDesc(x.mileage, y.mileage),
      );
      const groupMap = arr2groupmap(sorted, x => x.mileage);
      const res: Section[] = [];
      for (const [mileage, data] of groupMap) {
        res.push({
          mileage,
          data,
        });
      }
      return res;
    }, [medias]);

    const mapToMediaObject = useCallback((media: MediaWithSource) => {
      return mediaWithSourceToMediaObject(media, { preferOriginalUrl: true });
    }, []);

    if (medias == null) {
      return null;
    }

    if (!sections.length) {
      return (
        <EmptyPlaceholder
          text="暂无相关媒体文件"
          iconSize={rpx(80)}
          textStyle={{
            fontSize: rpx(28),
            lineHeight: rpx(48),
          }}
          css={`
            flex: 1;
          `}
        />
      );
    }

    if (sections.length === 1) {
      // multi column flat list
      const columns = Math.floor(width / (rpx(kMaxCellWidth) + cellMargin));
      const [cellWidth, cellHeight] = computeGridLayoutItemSize({
        numberOfColumns: columns,
        gridWidth: width + rpx(32) - paddingLeft - paddingRight,
        cellMargin: { left: 0, top: 0, bottom: 0, right: cellMargin },
        aspectRatio: 5 / 4,
        gridPadding: 0,
      });
      return (
        <>
          <SectionHeader
            mileage={sections[0].mileage}
            count={sections[0].data.length}
            css={`
              margin-bottom: ${rpx(32)}px;
              margin-left: ${paddingLeft}px;
              margin-right: ${paddingRight}px;
            `}
          />
          <MediaList
            data={medias}
            mapToMediaObject={mapToMediaObject}
            keyExtractor={x => x._id}
            numColumns={columns}
            showsVerticalScrollIndicator={Platform.OS === 'web'}
            renderItem={({ item, media, index, onPress }) => (
              <Cell
                item={item}
                media={media}
                index={index}
                onPress={onPress}
                css={`
                  width: ${cellWidth}px;
                  height: ${cellHeight}px;
                  margin-right: ${cellMargin}px;
                  margin-bottom: ${cellMargin}px;
                `}
              />
            )}
            css={`
              margin-left: ${paddingLeft}px;
              margin-right: ${paddingRight}px;
            `}
          />
        </>
      );
    } else {
      // section list
      const columns = Math.floor(width / (rpx(kMaxCellWidth) + cellMargin));
      const [cellWidth, cellHeight] = computeGridLayoutItemSize({
        numberOfColumns: columns + 0.5,
        gridWidth: width,
        cellMargin: { left: 0, top: 0, bottom: 0, right: cellMargin },
        aspectRatio: 5 / 4,
        gridPadding: 0,
      });
      return (
        <ScrollView showsVerticalScrollIndicator={Platform.OS === 'web'}>
          {sections.map(section => (
            <View key={section.mileage}>
              <SectionHeader
                mileage={section.mileage}
                count={section.data.length}
                css={`
                  margin-left: ${paddingLeft}px;
                  margin-right: ${paddingRight}px;
                `}
              />
              <MediaList
                data={section.data}
                mapToMediaObject={mediaWithSourceToMediaObject}
                keyExtractor={x => x._id}
                horizontal
                showsHorizontalScrollIndicator={Platform.OS === 'web'}
                contentContainerStyle={{
                  paddingTop: rpx(24),
                  paddingBottom: rpx(24),
                  paddingLeft,
                  paddingRight,
                }}
                renderItem={({ item, media, index, onPress }) => (
                  <Cell
                    item={item}
                    media={media}
                    index={index}
                    horizontal
                    onPress={onPress}
                    css={`
                      width: ${cellWidth}px;
                      height: ${cellHeight}px;
                      margin-right: ${cellMargin}px;
                      border-radius: ${rpx(32)}px;
                      overflow: hidden;
                      elevation: 3;
                    `}
                  />
                )}
                css={`
                  margin-bottom: ${rpx(32)}px;
                `}
              />
            </View>
          ))}
        </ScrollView>
      );
    }
  },
);

const SectionHeader = memo(
  ({
    mileage,
    count,
    style,
  }: {
    mileage: number;
    count: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity style={style}>
        <HStack justifyContent="flex-start">
          <Icon
            name="sheet-checked"
            size={rpx(36)}
            color={theme.task.medias.section.iconColor}
          />
          <Typography.Label
            medium
            color={theme.task.medias.section.titleColor}
            size={rpx(36)}
            lineHeight={rpx(48)}
            css={`
              margin-left: ${rpx(12)}px;
              margin-right: ${rpx(12)}px;
            `}
          >{`${mileage}公里 `}</Typography.Label>
          <Typography.Label
            medium
            color={theme.task.medias.section.numberColor}
            size={rpx(36)}
            lineHeight={rpx(48)}
          >{`${count}`}</Typography.Label>
        </HStack>
      </TouchableOpacity>
    );
  },
);

const MediaSourceLabel = memo(
  ({
    source,
    style,
  }: {
    source: MediaSourceType;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={style}>
        <Typography.Label regular size={rpx(28)} color={Colors.White6}>
          {MediaSourceTypeTextMap[source] ?? source}
        </Typography.Label>
      </View>
    );
  },
);

const Cell = memo(
  ({
    item,
    media,
    style,
    index,
    horizontal,
    onPress,
  }: {
    item: MediaWithSource;
    media: MediaObject;
    index: number;
    horizontal?: boolean;
    style?: StyleProp<ViewStyle>;
    onPress?: (media: MediaObject) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const handlePress = useCallback(() => {
      onPress?.(media);
    }, [media, onPress]);
    return (
      <MotiView
        from={{
          opacity: 0,
          translateX: horizontal ? 100 : 0,
        }}
        animate={{
          opacity: 1,
          translateX: 0,
        }}
        transition={{
          type: 'timing',
          duration: 350,
        }}
        delay={Math.min(1500, index * 50)}
        style={style}
      >
        <MediaCoverView
          media={media}
          TouchableComponent={TouchableOpacity}
          onPress={handlePress}
          activityIndicatorSize={rpx(80)}
          noPlaceholder={media.type === 'image' && media.url.includes('#')}
          containerStyle={{
            borderRadius: rpx(32),
            overflow: 'hidden',
          }}
          placeholderStyle={{
            backgroundColor: '#282828',
          }}
        >
          <View
            pointerEvents="none"
            css={`
              position: absolute;
              left: 0;
              right: 0;
              bottom: ${-rpx(6)}px;
              background-color: rgba(0, 0, 0, 0.75);
              min-height: ${rpx(100)}px;
              justify-content: center;
              padding-left: ${rpx(24)}px;
              padding-right: ${rpx(24)}px;
              padding-top: ${rpx(12)}px;
              padding-bottom: ${rpx(12)}px;
            `}
          >
            <MediaSourceLabel source={item.source} />
            {media.title ? (
              <Typography.Label
                regular
                size={rpx(22)}
                color={Colors.White5}
                numberOfLines={1}
              >
                {media.title}
              </Typography.Label>
            ) : null}
            {media.subTitle ? (
              <Typography.Label
                regular
                size={rpx(22)}
                color={Colors.White3_5}
                numberOfLines={1}
              >
                {media.subTitle}
              </Typography.Label>
            ) : null}
          </View>
        </MediaCoverView>
      </MotiView>
    );
  },
);
